import { ProjectApi, ProjectFilter, useProjectApi } from "../State";
import {
  PatterOccurrenceWithTags,
  PatterOccurrenceWithTagsPaginated,
  POS,
  TagType,
  VerbatimTag,
} from "../../../app_client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryClient } from "@tanstack/query-core";
import { useContext } from "react";
import { ProjectFilterContext } from "../ProjectFilterContext";

export const LexiquePatterns = [[POS.ADJ], [POS.VERB], [POS.NOUN]];
export const NgrammesPatterns = [
  [POS.VERB, POS.VERB],
  [POS.VERB, POS.NOUN],
  [POS.NOUN, POS.ADJ],
  [POS.ADJ, POS.NOUN],
];

export type patternsSearchParams = {
  posPatterns: POS[][] | undefined;
  term?: string | undefined;
  minMaxOccurrence: [number, number];
  page: number;
  enableParsing: boolean;
  semanticSearch: boolean;
  projectFilter: ProjectFilter;
};

export const usePatternQuery = ({
  projectId,
  searchParams,
}: {
  projectId: string;
  searchParams: patternsSearchParams;
}) => {
  const projectApi = useProjectApi() as ProjectApi;
  const queryClient = useQueryClient();

  const queryPatterns = async (p: number) => {
    let { posPatterns, term, minMaxOccurrence, enableParsing, semanticSearch, projectFilter } = searchParams;
    return await projectApi.getProjectPatterns(
      projectId,
      posPatterns,
      term,
      minMaxOccurrence[0],
      minMaxOccurrence[1],
      p,
      enableParsing,
      semanticSearch,
      projectFilter
    );
  };

  const _ProjectPatterns = useQuery({
    queryKey: ["_ProjectPatterns", projectId, searchParams],
    queryFn: () => {
      return queryPatterns(searchParams.page);
    },
    onSuccess: (data) => {
      prefetchNextPage();
    },
    enabled: !!projectApi,
    staleTime: 0,
  });

  const prefetchNextPage = () => {
    queryClient.prefetchQuery(
      [
        "_ProjectPatterns",
        projectId,
        {
          ...searchParams,
          page: searchParams.page + 1,
        },
      ],
      () => queryPatterns(searchParams.page + 1)
    );
  };

  const handleDeleteTag = async (
    pattern: PatterOccurrenceWithTags,
    tag: VerbatimTag
  ) => {
    if (projectApi && projectId) {
      await projectApi.deleteVerbatimTag(projectId, tag.id, true);
    }
    queryClient.setQueryData(
      ["_ProjectPatterns", projectId, searchParams],
      (oldData: PatterOccurrenceWithTagsPaginated | undefined) => {
        if (oldData === undefined) {
          return oldData;
        }
        return {
          ...oldData,
          data: removeTagFromPattern(oldData.data, pattern, tag),
        };
      }
    );
  };

  const handleTagAPattern = async (
    pattern: PatterOccurrenceWithTags,
    dimension: string,
    className: string,
    color: string
  ) => {
    queryClient.setQueryData(
      ["_ProjectPatterns", projectId, searchParams],
      (oldData: PatterOccurrenceWithTagsPaginated | undefined) => {
        if (oldData === undefined) {
          return oldData;
        }
        return {
          ...oldData,
          data: addTagToPatterns(
            oldData.data,
            pattern,
            className,
            dimension,
            color
          ),
        };
      }
    );
    await projectApi.tagAPattern({
      project_id: projectId,
      rule: pattern.pos_pattern.map((pos, i) => ({
        LEMMA: pattern.lemma.split(" ")[i],
        POS: pos,
      })),
      dimension: dimension,
      label: className || pattern.lemma,
    });
  };

  return {
    _ProjectPatterns,
    handleDeleteTag,
    handleTagAPattern,
    prefetchNextPage,
    prefecthPatterns,
  };
};

export const prefecthPatterns = (
  client: QueryClient,
  projectId: string,
  projectApi: ProjectApi,
  patterns: POS[][],
  projectFilters: ProjectFilter
) => {
  client.prefetchQuery(
    [
      "_ProjectPattern",
      projectId,
      {
        posPatterns: patterns,
        term: "",
        minMaxOccurrence: [1, 10000],
        page: 1,
      },
    ],
    () =>
      projectApi.getProjectPatterns(projectId, patterns, "", 1, 10000, 1, false, false, projectFilters)
  );
};

const removeTagFromPattern = (
  patterns: PatterOccurrenceWithTags[],
  pattern: PatterOccurrenceWithTags,
  tag: VerbatimTag
) => {
  return patterns.map((p) => {
    if (p.id === pattern.id) {
      return {
        ...p,
        tags: (p.tags || []).filter((t) => t.id !== tag.id),
      };
    } else {
      return p;
    }
  });
};

const addTagToPatterns = (
  patterns: PatterOccurrenceWithTags[],
  pattern: PatterOccurrenceWithTags,
  className: string,
  dimension: string,
  color: string
) => {
  return patterns.map((p: PatterOccurrenceWithTags) => {
    if (p.id === pattern.id) {
      return {
        ...p,
        tags: [
          ...(p.tags || []),
          {
            id: "fake_id",
            verbatim_id: "fake_verbatim_id",
            type: TagType.TOKEN_CLASSIFICATION,
            dimension: dimension,
            label: className || pattern.lemma,
            color: color,
            score: 1,
          },
        ],
      };
    } else {
      return p;
    }
  });
};



export const getFrenchPOSName = (pos: POS): string => {
  switch (pos) {
    case POS.ADJ:
      return "Adjectif";
    case POS.ADP:
      return "Adposition";
    case POS.ADV:
      return "Adverbe";
    case POS.AUX:
      return "Auxiliaire";
    case POS.CONJ:
      return "Conjonction";
    case POS.CCONJ:
      return "Conjonction de coordination";
    case POS.DET:
      return "Déterminant";
    case POS.INTJ:
      return "Interjection";
    case POS.NOUN:
      return "Nom";
    case POS.NUM:
      return "Numéral";
    case POS.PART:
      return "Particule";
    case POS.PRON:
      return "Pronom";
    case POS.PROPN:
      return "Nom propre";
    case POS.PUNCT:
      return "Ponctuation";
    case POS.SCONJ:
      return "Conjonction de subordination";
    case POS.SYM:
      return "Symbole";
    case POS.VERB:
      return "Verbe";
    case POS.X:
      return "Inconnu";
    default:
      return "Inconnu";
  }
};
